import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useInviteUser } from '../../hooks/useUserInvitations';
import { AppRole } from '../../types/organization.types';
import { UserPlus, Mail, Shield, Info, SendHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';

const ROLES: { value: AppRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'talent_manager', label: 'Talent Manager' },
  { value: 'content_strategist', label: 'Content Strategist' },
  { value: 'account_executive', label: 'Account Executive' },
  { value: 'finance', label: 'Finance' },
  { value: 'account_manager', label: 'Account Manager' },
];

export interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const InviteUserDialog: React.FC<InviteUserDialogProps> = ({ open, onOpenChange }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AppRole>('account_manager');
  const [errors, setErrors] = useState<{ email?: string; role?: string }>({});

  const inviteUser = useInviteUser();

  const validateForm = () => {
    const newErrors: { email?: string; role?: string } = {};
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!role) {
      newErrors.role = 'Role is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    inviteUser.mutate(
      { email: email.trim(), role },
      {
        onSuccess: () => {
          setEmail('');
          setRole('account_manager');
          setErrors({});
          onOpenChange(false);
        },
      }
    );
  };

  const handleClose = () => {
    setEmail('');
    setRole('account_manager');
    setErrors({});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] rounded-[24px] p-6 border-0 bg-white shadow-2xl gap-0">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Header section */}
          <div className="flex items-start gap-4 pb-5 border-b border-gray-100 pr-6">
            <div className="w-12 h-12 rounded-2xl bg-[#F5EEFE] flex items-center justify-center shrink-0">
              <UserPlus className="w-6 h-6 text-[#9333EA]" />
            </div>
            <div className="space-y-1">
              <DialogTitle className="text-xl font-bold text-gray-900 tracking-tight">
                Invite New User
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm text-gray-500 font-normal leading-relaxed">
                Send an invitation to a new user to join your organization. They will receive an email with instructions to complete their account setup.
              </DialogDescription>
            </div>
          </div>
          
          {/* Form Content */}
          <div className="space-y-5">
            {/* Email Field */}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs sm:text-sm font-semibold text-gray-900">
                Email Address
              </Label>
              <div className={cn(
                "flex items-center rounded-xl border overflow-hidden transition-all bg-white",
                errors.email 
                  ? "border-red-500 focus-within:ring-1 focus-within:ring-red-500" 
                  : "border-[#A855F7]/70 focus-within:border-[#9333EA] focus-within:ring-2 focus-within:ring-purple-100"
              )}>
                <div className="w-12 h-11 bg-[#F5EEFE] flex items-center justify-center shrink-0 border-r border-purple-100">
                  <Mail className="w-5 h-5 text-[#9333EA]" />
                </div>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) {
                      setErrors(prev => ({ ...prev, email: undefined }));
                    }
                  }}
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-11 px-3.5 text-sm text-gray-900 placeholder:text-gray-400 bg-transparent w-full"
                />
              </div>
              {errors.email ? (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              ) : (
                <p className="text-xs text-gray-500">Enter the email address of the person you want to invite.</p>
              )}
            </div>
            
            {/* Role Field */}
            <div className="space-y-1.5">
              <Label htmlFor="role" className="text-xs sm:text-sm font-semibold text-gray-900">
                Role
              </Label>
              <div className={cn(
                "flex items-center rounded-xl border overflow-hidden transition-all bg-white",
                errors.role 
                  ? "border-red-500" 
                  : "border-gray-200 focus-within:border-[#9333EA] focus-within:ring-2 focus-within:ring-purple-100 hover:border-purple-200"
              )}>
                <div className="w-12 h-11 bg-[#F5EEFE] flex items-center justify-center shrink-0 border-r border-purple-100">
                  <Shield className="w-5 h-5 text-[#9333EA]" />
                </div>
                <Select 
                  value={role} 
                  onValueChange={(value: AppRole) => {
                    setRole(value);
                    if (errors.role) {
                      setErrors(prev => ({ ...prev, role: undefined }));
                    }
                  }}
                >
                  <SelectTrigger className="border-0 focus:ring-0 focus:ring-offset-0 h-11 px-3.5 text-sm text-gray-900 bg-transparent w-full">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(roleOption => (
                      <SelectItem key={roleOption.value} value={roleOption.value}>
                        {roleOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {errors.role ? (
                <p className="text-xs text-red-500 mt-1">{errors.role}</p>
              ) : (
                <p className="text-xs text-gray-500">Choose the appropriate role for this user.</p>
              )}
            </div>

            {/* Informational Box */}
            <div className="bg-[#F5EEFE]/60 rounded-2xl p-4 flex items-start space-x-3.5 border border-purple-100/60">
              <div className="w-7 h-7 rounded-full bg-[#9333EA] text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm">
                <Info className="w-4 h-4" />
              </div>
              <div className="space-y-0.5">
                <h4 className="text-sm font-semibold text-gray-900">What happens next?</h4>
                <p className="text-xs text-gray-600 leading-relaxed">
                  We'll send an email invitation with setup instructions. The user must accept the invitation to join.
                </p>
              </div>
            </div>
          </div>
          
          {/* Footer Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              className="rounded-xl px-5"
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              variant="default"
              disabled={inviteUser.isPending}
              className="rounded-xl px-5"
            >
              <SendHorizontal className="w-4 h-4" />
              {inviteUser.isPending ? 'Sending Invitation...' : 'Send Invitation'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserDialog;
